'use client'

import { use, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notFound, useParams, useRouter } from 'next/navigation'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'

import { ConfigDrivenStep } from '@/src/components/flow/ConfigDrivenStep'
import { bookingStudentFlow } from '@/src/config/flows/booking-student.flow'
import {
  bookingWorkerFlow,
  getWorkerStepById,
  getWorkerStepBySlug
} from '@/src/config/flows/booking-worker.flow'
import {
  resolveFallbackUrl,
  resolveSuccessRedirectUrl
} from '@/src/lib/flow-engine/resolveSuccessRedirect'
import { findFirstIncompleteStep } from '@/src/lib/flow-engine/guardUtils'
import { getPivotFields } from '@/src/lib/flow-engine/getPivotFields'
import { restoreDraftToStore } from '@/src/lib/flow-engine/restoreDraft'
import { useStudentBookingStore } from '@/src/lib/store/studentBookingStore'
import { useWorkerBookingStore } from '@/src/lib/store/workerBookingStore'
import { submitWorkerBooking } from '@/src/services/booking-worker.api'
import { AdmissionSaveError } from '@/src/services/admission-save-error'

interface Props {
  params: Promise<{ locale: string; step: string }>
}

export default function WorkerStepPage({ params }: Props) {
  const { step } = use(params)
  const { locale } = useParams<{ locale: string }>()
  const router = useRouter()
  const { i18n, t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const stepConfig = getWorkerStepBySlug(step)

  if (!stepConfig) {
    notFound()
  }

  const {
    draftId,
    setDraftId,
    formData,
    fieldLabels,
    updateStepData,
    updateFieldLabels,
    markStepCompleted,
    setCurrentStep,
    clearStepsAfter,
    resetStore
  } = useWorkerBookingStore()

  const allSteps = bookingWorkerFlow.steps
  const isFirstStep = stepConfig.id === 1

  // Block access if any previous step has unfilled required (visible) fields
  useEffect(() => {
    if (stepConfig.id > 1) {
      const { formData: currentFormData } = useWorkerBookingStore.getState()
      const incompleteStep = findFirstIncompleteStep(
        bookingWorkerFlow.steps,
        currentFormData,
        stepConfig.id
      )
      if (incompleteStep) {
        router.push(`/${locale}/booking-worker/${incompleteStep.slug}/`)
      }
    }
  }, [stepConfig.id, router, locale])

  useEffect(() => {
    if (draftId) return

    const params = new URLSearchParams(window.location.search)
    const queryDraftId = params.get('draftId')
    if (queryDraftId) {
      setDraftId(queryDraftId)
    }
  }, [draftId, setDraftId])

  const studentStore = useStudentBookingStore()

  const handleDraftRestored = useCallback(
    (
      restoredDraftId: string,
      apiSteps: { values: { name: string; value: string }[] }[],
      isWorker: boolean
    ): string => {
      const flow = isWorker ? bookingWorkerFlow : bookingStudentFlow
      const actions = isWorker
        ? { setDraftId, resetStore, updateStepData, markStepCompleted, setCurrentStep }
        : {
            setDraftId: studentStore.setDraftId,
            resetStore: studentStore.resetStore,
            updateStepData: studentStore.updateStepData,
            markStepCompleted: studentStore.markStepCompleted,
            setCurrentStep: studentStore.setCurrentStep
          }

      return restoreDraftToStore(restoredDraftId, apiSteps, flow, actions)
    },
    [setDraftId, resetStore, updateStepData, markStepCompleted, setCurrentStep, studentStore]
  )

  const handleSubmit = async (data: Record<string, unknown>) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setSaveError(null)

    const stepKey = `step${stepConfig.id}`
    const shouldTerminate = Boolean(data.__terminateBooking)

    // If a pivot field changed (fields that control visibility/options of later steps),
    // clear all subsequent steps to avoid stale/invalid data
    if (!stepConfig.isFinal) {
      const previousData = formData[stepKey] ?? {}
      if (Object.keys(previousData).length > 0) {
        const pivotFields = getPivotFields(allSteps, stepConfig.id)
        const pivotChanged = [...pivotFields].some(
          (key) => JSON.stringify(data[key]) !== JSON.stringify(previousData[key])
        )
        if (pivotChanged) {
          clearStepsAfter(stepConfig.id)
        }
      }
    }

    // When a full-structure alternative changes city, propagate to the owning step
    if (data.citta_1 && stepConfig.slug !== 'city') {
      updateStepData('step3', { citta_1: data.citta_1 })
    }

    const nextFormData = {
      ...formData,
      [stepKey]: {
        ...formData[stepKey],
        ...data
      },
      // Ensure city step reflects the alternative city in the payload
      ...(data.citta_1 && stepConfig.slug !== 'city'
        ? { step3: { ...formData['step3'], citta_1: data.citta_1 } }
        : {})
    }

    updateStepData(stepKey, data)
    markStepCompleted(stepConfig.id)

    try {
      if (shouldTerminate) {
        const saveResponse = await submitWorkerBooking({
          journey: 'worker',
          formData: nextFormData,
          submittedAt: new Date().toISOString(),
          currentStep: stepConfig.id,
          finalStep: false,
          draftId,
          lang: (i18n.resolvedLanguage ?? 'it').slice(0, 2)
        })
        if (saveResponse.data.body) {
          setDraftId(String(saveResponse.data.body))
        }
        resetStore()

        // Resolve success redirect URL or fallback to configured fallback URL
        const resolvedLang = (i18n.resolvedLanguage ?? 'it').slice(0, 2)
        const successUrl = resolveSuccessRedirectUrl(
          bookingWorkerFlow.successRedirectRules,
          nextFormData,
          resolvedLang
        )
        if (successUrl) {
          window.location.href = successUrl
        } else {
          const fallbackUrl = resolveFallbackUrl(bookingWorkerFlow.fallbackUrl, resolvedLang)
          if (fallbackUrl) {
            window.location.href = fallbackUrl
          } else {
            router.push(`/${locale}/booking-worker/success/`)
          }
        }
        return
      }

      // If final step, submit the booking
      if (stepConfig.isFinal) {
        const saveResponse = await submitWorkerBooking({
          journey: 'worker',
          formData: nextFormData,
          submittedAt: new Date().toISOString(),
          currentStep: stepConfig.id,
          finalStep: true,
          draftId,
          lang: (i18n.resolvedLanguage ?? 'it').slice(0, 2)
        })
        if (saveResponse.data.body) {
          setDraftId(String(saveResponse.data.body))
        }
        resetStore()

        // Resolve success redirect URL or fallback to configured fallback URL
        const resolvedLang = (i18n.resolvedLanguage ?? 'it').slice(0, 2)
        const successUrl = resolveSuccessRedirectUrl(
          bookingWorkerFlow.successRedirectRules,
          nextFormData,
          resolvedLang
        )
        if (successUrl) {
          window.location.href = successUrl
        } else {
          const fallbackUrl = resolveFallbackUrl(bookingWorkerFlow.fallbackUrl, resolvedLang)
          if (fallbackUrl) {
            window.location.href = fallbackUrl
          } else {
            router.push(`/${locale}/booking-worker/success/`)
          }
        }
        return
      }

      if (stepConfig.saveOnNext) {
        const saveResponse = await submitWorkerBooking({
          journey: 'worker',
          formData: nextFormData,
          submittedAt: new Date().toISOString(),
          currentStep: stepConfig.id,
          finalStep: false,
          draftId,
          lang: (i18n.resolvedLanguage ?? 'it').slice(0, 2)
        })
        if (saveResponse.data.body) {
          setDraftId(String(saveResponse.data.body))
        }
      }

      // Navigate to next step
      const nextStep = getWorkerStepById(stepConfig.id + 1)
      if (nextStep) {
        setCurrentStep(nextStep.id)
        router.push(`/${locale}/booking-worker/${nextStep.slug}/`)
      }
    } catch (error) {
      if (error instanceof AdmissionSaveError) {
        setSaveError(error.message)
      } else {
        setSaveError(t('common.saveError'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    const prevStep = getWorkerStepById(stepConfig.id - 1)
    if (prevStep) {
      setCurrentStep(prevStep.id)
      router.push(`/${locale}/booking-worker/${prevStep.slug}/`)
    }
  }

  return (
    <>
      {saveError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        </Box>
      )}
      <ConfigDrivenStep
        stepConfig={stepConfig}
        allSteps={allSteps}
        savedFormData={formData}
        fieldLabels={fieldLabels}
        onSubmit={handleSubmit}
        onFieldLabelResolved={updateFieldLabels}
        onDraftRestored={handleDraftRestored}
        onBack={handleBack}
        isFirstStep={isFirstStep}
        isSubmitting={isSubmitting}
      />
    </>
  )
}
